// PINNED BATCHES — the leads an operation showed last time, in the order it
// showed them.
//
// ── THE RULE (user, 2026-08-22) ─────────────────────────────────────────────
// Initial outreach, the follow-up and the daily worklist must hand back THE
// SAME leads in THE SAME order, run after run, until a lead is done or has
// been reached out to. A rep works a list top to bottom across several
// sittings; a list that reshuffles between sittings is a list that gets
// worked twice in one place and never in another.
//
// Ranking alone cannot keep that promise. compareCandidates IS a total order,
// but the inputs to it move under a rep's feet: the initial-outreach research
// reorder promotes whichever leads the workspace has researched since the
// last run, the follow-up's due set is recomputed every morning (a newly-due
// lead with a higher score pushes yesterday's leads down or off the cap), and
// the pools themselves change as leads are imported or emailed. Each run was
// deterministic; the sequence of runs was not.
//
// So each operation remembers the batch it showed — keys only, in order —
// and the next run CARRIES that batch forward first:
//
//   1. Every pinned lead that is still eligible keeps its place, in the pinned
//      order, ahead of everything else.
//   2. A pinned lead the run considered but no longer finds eligible is
//      RETIRED: it was reached out to (the gate now sees a touch), it replied
//      (frozen), it finished the cadence, it said no, it sits in a deal. The
//      step that dropped it says why in the lead's own trace. It leaves the
//      pin, and if it ever becomes eligible again (a follow-up three business
//      days after today's touch) it re-enters at the END, as a new lead.
//   3. A pinned lead the run did NOT consider at all (a scoped run —
//      "/initial-outreach TX" — or a history source that answered partially)
//      is HELD: not shown, not retired, still pinned. Out of scope is not done.
//   4. Free slots under the cap fill from the run's own ranking, skipping
//      anything already pinned, and the fills are appended to the pin.
//
// ── WHAT THIS IS NOT ────────────────────────────────────────────────────────
// It decides ORDER, never ELIGIBILITY. A pinned lead that fails a gate is
// gone, full stop — the pin can never put a contacted company back on a cold
// intro, or a replied lead back on the cadence. It is also not a record of
// work done: "copied" and "sent" live elsewhere (copiedDrafts.js, the HubSpot
// mirror); this file only knows what was SHOWN.
//
// Per user, per operation, per lead category, browser-local (scopedKey — the
// same store family as the daily-task ledger and the copied marks). Bounded:
// at most PIN_LIMIT keys per operation. Pure logic in pinOrder(); the store is
// the thin layer around it. No React, no network, no Math.random; node-testable.
import { scopedKey } from './userScope.js'

const STORAGE_BASE = 'revailosales.batchPin'
const STORE_VERSION = 1

// The most keys one operation keeps pinned. A batch is 40–50; the rest is
// room for held (out-of-scope) keys and carried leads that sit below the cap.
export const PIN_LIMIT = 500

function safeStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

// One pin per operation per category: the follow-up's batch and the
// worklist's batch are different lists even when they overlap, and a masonry
// batch is not a roofing batch.
export function pinIdOf(operationId, category) {
  return `${String(operationId || '')}::${String(category || '')}`
}

function normalizeKeys(raw) {
  if (!Array.isArray(raw)) return []
  const out = []
  const seen = new Set()
  for (const k of raw) {
    const key = String(k ?? '').trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(key)
    if (out.length >= PIN_LIMIT) break
  }
  return out
}

function read() {
  const store = safeStorage()
  if (!store) return { version: STORE_VERSION, ops: {} }
  let parsed
  try {
    const raw = store.getItem(scopedKey(STORAGE_BASE))
    parsed = raw ? JSON.parse(raw) : null
  } catch {
    return { version: STORE_VERSION, ops: {} }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { version: STORE_VERSION, ops: {} }
  }
  const rawOps = parsed.ops
  if (!rawOps || typeof rawOps !== 'object' || Array.isArray(rawOps)) {
    return { version: STORE_VERSION, ops: {} }
  }
  const ops = {}
  for (const [id, entry] of Object.entries(rawOps)) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
    const keys = normalizeKeys(entry.keys)
    if (!keys.length) continue
    ops[id] = { keys, at: typeof entry.at === 'string' ? entry.at : '' }
  }
  return { version: STORE_VERSION, ops }
}

function write(state) {
  const store = safeStorage()
  if (!store) return
  try {
    store.setItem(scopedKey(STORAGE_BASE), JSON.stringify({ version: STORE_VERSION, ops: state.ops || {} }))
  } catch {
    // Quota or private mode: the next run simply starts from its own ranking.
    // Never a throw into the pipeline.
  }
}

/** The pinned batch for an operation: `{ keys: string[], at: ISO|'' }`. */
export function readPin(operationId, category) {
  const entry = read().ops[pinIdOf(operationId, category)]
  return entry ? { keys: entry.keys.slice(), at: entry.at } : { keys: [], at: '' }
}

/** Replace the pinned batch. Returns the keys actually stored (deduped, capped). */
export function savePin(operationId, category, keys, { now = new Date() } = {}) {
  const stored = normalizeKeys(keys)
  const state = read()
  const id = pinIdOf(operationId, category)
  if (stored.length) {
    const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date()
    state.ops[id] = { keys: stored, at: at.toISOString() }
  } else {
    delete state.ops[id]
  }
  write(state)
  return stored
}

/** Forget an operation's batch — the next run builds a fresh one from rank. */
export function clearPin(operationId, category) {
  const state = read()
  const id = pinIdOf(operationId, category)
  if (!state.ops[id]) return false
  delete state.ops[id]
  write(state)
  return true
}

/**
 * The order a run must take, given what it pinned last time and what it finds
 * eligible now. PURE — reads nothing, writes nothing; the caller persists
 * `nextPin` once the batch is cut.
 *
 * @param {object} args
 * @param {string[]} args.pinned    keys shown last time, in order
 * @param {Array}    args.eligible  this run's eligible items, in THIS run's
 *                                  rank order (the order new leads fill in)
 * @param {(item:any)=>string} args.keyOf
 * @param {(key:string)=>boolean} [args.inScope]
 *        whether the run considered this key at all. Absent = every pinned
 *        key was considered, so any that is not eligible is retired.
 * @param {number}   args.cap       how many the run takes
 * @returns {{
 *   sequence: Array,      every eligible item, carried first (pinned order) then the rest (rank order)
 *   taken: Array,         sequence.slice(0, cap)
 *   carried: Array,       pinned items still eligible, in pinned order (may exceed cap)
 *   carriedTaken: number, how many of `taken` were carried
 *   fresh: Array,         items in `taken` that were not pinned before
 *   retired: string[],    pinned keys considered this run and no longer eligible
 *   held: string[],       pinned keys the run did not consider (kept pinned, not shown)
 *   nextPin: string[],    what to persist: pinned minus retired, then the fresh fills
 * }}
 */
export function pinOrder({ pinned, eligible, keyOf, inScope, cap }) {
  const limit = Number.isFinite(Number(cap)) && Number(cap) >= 0 ? Math.floor(Number(cap)) : Infinity
  const key = (item) => String((typeof keyOf === 'function' ? keyOf(item) : item) ?? '')
  const considered = typeof inScope === 'function' ? inScope : () => true

  // First occurrence wins: two eligible entries for one lead are one lead.
  const byKey = new Map()
  for (const item of eligible || []) {
    const k = key(item)
    if (k && !byKey.has(k)) byKey.set(k, item)
  }

  const carried = []
  const retired = []
  const held = []
  const keptPinned = [] // pinned keys that stay pinned, in their original order
  const seen = new Set()
  for (const raw of normalizeKeys(pinned)) {
    if (seen.has(raw)) continue
    seen.add(raw)
    const item = byKey.get(raw)
    if (item) {
      carried.push(item)
      keptPinned.push(raw)
    } else if (considered(raw)) {
      retired.push(raw)
    } else {
      held.push(raw)
      keptPinned.push(raw)
    }
  }

  const rest = []
  const restSeen = new Set(seen)
  for (const [k, item] of byKey) {
    if (restSeen.has(k)) continue
    restSeen.add(k)
    rest.push(item)
  }

  const sequence = [...carried, ...rest]
  const taken = sequence.slice(0, limit)
  const carriedTaken = Math.min(carried.length, taken.length)
  const fresh = taken.slice(carriedTaken)
  const nextPin = normalizeKeys([...keptPinned, ...fresh.map(key)])

  return { sequence, taken, carried, carriedTaken, fresh, retired, held, nextPin }
}

/**
 * The one sentence a step's detail line says about the pin, or '' when the
 * run built its batch from scratch (nothing was pinned before).
 */
export function pinSummaryLine({ carriedTaken, fresh, retired, held, pinnedBefore }) {
  if (!pinnedBefore) {
    return 'First batch for this list — pinned from here: the next run shows these same leads in this same order until each is done or reached out to.'
  }
  const parts = []
  parts.push(
    carriedTaken
      ? `${carriedTaken} carried over from the last run in the same order — a lead keeps its place until it is done or reached out to`
      : 'none of the last run’s leads are still eligible',
  )
  if (retired.length) {
    parts.push(
      `${retired.length} left the list (reached out to, replied, finished the cadence, said no, or in a deal — each lead’s own trace says which)`,
    )
  }
  if (fresh.length) parts.push(`${fresh.length} new at the end to fill the freed ${fresh.length === 1 ? 'slot' : 'slots'}`)
  if (held.length) {
    parts.push(
      `${held.length} pinned ${held.length === 1 ? 'lead is outside this run’s scope and stays' : 'leads are outside this run’s scope and stay'} pinned unseen`,
    )
  }
  return `${parts.join('; ')}.`
}
